'use client'

import { useMemo, useState, type ReactNode } from 'react'
import AppIcon from '@/components/content/app/AppIcon'
import Link from '@/components/ui/Link'
import Overview from './Overview'
import Sheet from './Sheet'
import { Icon } from '@/icons/Icon'
import { getBrandColors, cn, determineEfficiencyLabels, isPopulatedObject } from '@/lib/utils'
import { extractUniqueCriteriaFromApp } from '@/lib/formatting'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { App, Category, Media, Criterion, CategoryCriteriaCriterion } from '@/payload-types'

export type CriterionWithRating = Criterion & {
  categoryCriteria?: CategoryCriteriaCriterion
  rating?: number | null
  isApplicable?: boolean
}

export type CriteriaOverviewProps = {
  children?: ReactNode | ((isVisible: boolean) => ReactNode)
  app: Pick<
    App,
    | 'id'
    | 'name'
    | 'slug'
    | 'icon'
    | 'glyph'
    | 'brandBackgroundColor'
    | 'brandPrimaryColor'
    | 'categoryRecommendations'
  >
  criteria?: CriterionWithRating[]
  categoryId?: Category['id']
  reviewCriterionRatings?: Array<{
    categoryCriterion?: CategoryCriteriaCriterion['id'] | CategoryCriteriaCriterion | null
    rating?: number | null
    isApplicable?: boolean | null
  }>
  horizontalAlign?: 'left' | 'center' | 'right'
  verticalAlign?: 'top' | 'center' | 'bottom'
  hideDescription?: boolean
  disabled?: boolean
  className?: string
}

export default function CriteriaOverview({
  children,
  app,
  criteria,
  categoryId,
  reviewCriterionRatings,
  horizontalAlign = 'center',
  verticalAlign = 'top',
  hideDescription = false,
  disabled = false,
  className,
}: CriteriaOverviewProps) {
  const criteriaContent = (
    <CriteriaOverviewContent
      app={app}
      criteria={criteria}
      categoryId={categoryId}
      reviewCriterionRatings={reviewCriterionRatings}
      hideDescription={hideDescription}
    />
  )
  const { md } = useBreakpoint()

  const [sheetVisible, setSheetVisible] = useState(false)
  const [overviewVisible, setOverviewVisible] = useState(false)

  const renderChildren = (isVisible: boolean) => {
    return typeof children === 'function' ? children(isVisible) : children
  }

  if (disabled) {
    return renderChildren(false)
  }

  if (!md) {
    return (
      <Sheet
        trigger={<div onClick={() => setSheetVisible(true)}>{renderChildren(sheetVisible)}</div>}
        isVisible={sheetVisible}
        isMenu={true}
        close={() => setSheetVisible(false)}
      >
        <div className="px-3 pb-3">{criteriaContent}</div>
      </Sheet>
    )
  }

  return (
    <Overview
      content={criteriaContent}
      horizontalAlign={horizontalAlign}
      verticalAlign={verticalAlign}
      className={className}
      onVisibilityChange={setOverviewVisible}
    >
      {renderChildren(overviewVisible)}
    </Overview>
  )
}

export function CriteriaOverviewContent({
  app,
  criteria,
  categoryId,
  reviewCriterionRatings,
  hideDescription = false,
}: {
  app: CriteriaOverviewProps['app']
  criteria?: CriterionWithRating[]
  categoryId?: Category['id']
  reviewCriterionRatings?: CriteriaOverviewProps['reviewCriterionRatings']
  hideDescription?: boolean
}) {
  const { md } = useBreakpoint()
  const criterias = useMemo(
    () => criteria ?? extractUniqueCriteriaFromApp(app, reviewCriterionRatings, categoryId),
    [app, criteria, categoryId, reviewCriterionRatings],
  )

  const singleCriterion = criterias?.length === 1 ? criterias[0] : null
  const allUnrated =
    criterias.length > 0 &&
    criterias.every(
      (c) => c.isApplicable !== false && (c.rating === null || c.rating === undefined),
    )
  const efficiencyLabels = useMemo(() => {
    if (singleCriterion) {
      if (singleCriterion.isApplicable === false) {
        return {
          tier: 'is not applicable',
          description: 'This criterion does not apply to this app.',
        }
      }
      if (singleCriterion.rating == null) {
        return {
          tier: 'is not yet rated',
          description: 'This app has not been rated in this category yet.',
        }
      }
      return determineEfficiencyLabels(singleCriterion.rating)
    }
    if (allUnrated) {
      return {
        tier: 'is not yet rated',
        description: 'This app has not been rated in this category yet.',
      }
    }
    return determineEfficiencyLabels(undefined)
  }, [singleCriterion, allUnrated])

  const appIcon = app?.icon as Media
  const appGlyph = app?.glyph as Media
  const hasIcon = isPopulatedObject<Media>(appIcon) || isPopulatedObject<Media>(appGlyph)
  const { backgroundColor } = getBrandColors(app)

  const allRatingsUndefined = criterias.every(
    (c) => c.isApplicable === false || c.rating === null || c.rating === undefined,
  )
  const totalEfficiencyPoints = allRatingsUndefined
    ? null
    : criterias.filter((c) => c.isApplicable !== false && (c.rating ?? 0) >= 5).length

  const appBar = (barPosition: 'top' | 'center' | 'bottom') => (
    <div
      className={cn(
        barPosition === 'top' ? 'rounded-t-xl border-b' : 'rounded-b-xl border-t',
        'flex h-11 w-full items-center gap-3 pl-3',
        'border-gray-200 bg-gray-50 dark:border-white/15 dark:bg-white/5',
      )}
    >
      {hasIcon && (
        <AppIcon
          glyph={isPopulatedObject<Media>(appGlyph) ? appGlyph : undefined}
          icon={isPopulatedObject<Media>(appIcon) ? appIcon : undefined}
          name={app.name}
          background={backgroundColor}
          size={24}
          className="shadow-subtle"
        />
      )}
      <div className="flex items-center gap-1.5 text-14">
        <span className="whitespace-nowrap font-medium text-gray-950 dark:text-white">
          {app.name}
        </span>
        <span className="truncate text-gray-500 dark:text-zinc-400">{efficiencyLabels.tier}</span>
      </div>

      <Overview
        content={<InfoTooltip text={efficiencyLabels.description} points={totalEfficiencyPoints} />}
        horizontalAlign={md ? 'right' : 'left'}
        verticalAlign="top"
        className="relative ml-auto flex items-center pr-3"
      >
        <div
          tabIndex={0}
          role="button"
          aria-label="Show efficiency information"
          className="flex h-6 w-6 shrink-0 items-center justify-center text-gray-400 transition-colors hover:text-gray-950 dark:text-white/30 dark:hover:text-white"
        >
          <Icon name="circle-info-sm" size={12} />
        </div>
      </Overview>
    </div>
  )

  return (
    <div className="w-full">
      <div
        className={cn(
          'flex h-fit w-full cursor-default flex-col',
          'rounded-xl border md:shadow-xl',
          'border-gray-200 bg-white dark:border-white/15 dark:bg-black',
        )}
      >
        {(criterias.length > 1 || criterias.length === 0) && appBar('top')}
        <div className="flex h-full flex-col gap-2 px-2 py-1.5">
          {criterias &&
            criterias.map((c, i) => (
              <div key={`${app.slug}-${c.id}-${i}`} className="flex gap-3 px-1 py-1.5">
                <div
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-100 dark:border-white/10 dark:bg-white/5',
                    'text-gray-600 dark:text-white/80',
                  )}
                >
                  <Icon name={c.icon as string} />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'whitespace-nowrap text-14 font-medium',
                        'text-gray-950 dark:text-white',
                      )}
                    >
                      {c.name}
                    </span>
                    {c.isApplicable === false ? (
                      <span className="text-12 text-gray-400 dark:text-zinc-500">N/A</span>
                    ) : c.rating !== null && c.rating !== undefined ? (
                      <RatingScale rating={c.rating} />
                    ) : null}
                  </div>

                  {!hideDescription && c?.categoryCriteria?.description && (
                    <p className="w-[285px] pr-1 text-13 text-gray-500 dark:text-zinc-400">
                      {c.categoryCriteria.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
        </div>
        {criterias.length === 1 && appBar('bottom')}
      </div>
    </div>
  )
}

export function RatingScale({ rating }: { rating: number }) {
  const { md } = useBreakpoint()

  const defaultColor =
    rating >= 5 ? 'bg-primary-500/30 dark:bg-primary-500/40' : 'bg-slate-200 dark:bg-white/30'
  const activeColor =
    rating >= 5 ? 'bg-primary-500 dark:bg-primary-500' : 'bg-slate-500 dark:bg-white'

  const efficiencyLabels = determineEfficiencyLabels(rating)

  return (
    <Overview
      horizontalAlign={md ? 'right' : 'center'}
      verticalAlign={md ? 'center' : 'bottom'}
      content={<InfoTooltip text={efficiencyLabels.description} rating={rating} />}
    >
      <div
        tabIndex={0}
        role="button"
        aria-label="Show rating information"
        className={cn(
          'flex items-center gap-0.5 rounded-key px-1.5 py-1 transition-colors [&>span]:h-3 [&>span]:w-0.5 [&>span]:rounded-[0.5px]',
          rating >= 5
            ? 'hover:bg-primary-500/5 dark:hover:bg-primary-500/15'
            : 'hover:bg-slate-500/5 dark:hover:bg-white/10',
        )}
      >
        {Array.from({ length: 7 }, (_, i) => i + 1).map((i) => (
          <span key={i} className={cn(defaultColor, rating >= i && activeColor)}></span>
        ))}
      </div>
    </Overview>
  )
}

export function InfoTooltip({
  text,
  points,
  rating,
}: {
  text: string
  points?: number | null
  rating?: number | null
}) {
  const showRating = rating !== null && rating !== undefined
  const showPoints = points !== null && points !== undefined

  const infoLabel = showRating
    ? `${rating} of 7 points`
    : showPoints
      ? `${points} efficiency point${points === 1 ? '' : 's'}`
      : `Not yet rated`

  return (
    <div className="flex w-[240px] flex-col rounded-xl border border-white/20 bg-black px-3 py-2 text-12 leading-loose shadow-md">
      <p className="font-medium text-white">{infoLabel}</p>
      <p className="text-zinc-400">
        {text}{' '}
        <Link href="/criteria" className="underlined primary">
          See our full scoring method
        </Link>
      </p>
    </div>
  )
}
